import { zoomTransform, event, select, axisRight } from "d3";
import {
  inRange,
  mouseDate,
  roundDate,
  zoomSetup
} from "@/_core/charts/TechnicalAnalysis";
import { displayDataCOT } from "./CommitmentTraders";

export function NetHover(_this, barWidth = 0.7, D3Chart) {
  const auxName = "Net";

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName)._groups[0][0]
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(
        event.layerX - _this.svgNodeNet.margin.left,
        _this.svgNodeNet.width
      ),
      0
    );
    let xt = _this.transform.rescaleX(_this.svgNodeNet.x);
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (
      dat <= _this.allData.historical[_this.allData.historical.length - 1].date
    ) {
      selData = mouseDate(xt.invert(xPixels), _this.allData.historical);
    } else {
      selData = JSON.parse(
        JSON.stringify(
          _this.allData.historical[_this.allData.historical.length - 1]
        )
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(
        event.layerY - _this.svgNodeNet.margin.top,
        _this.svgNodeNet.height
      ),
      0
    );
    let bandWidth = _this.transform.k * _this.bandWidth;
    const candleWidth = bandWidth * barWidth + 1;
    let xPix = xt(selData.date);

    // Showing elements
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.show({
        yPixels,
        xPix,
        selData,
        dateFormat: _this.dateFormat,
        xt,
        candleWidth,
        highlightCandles,
        className: hoverElement.className
      });
    }

    select(".vHoverLinecommitmentTraders")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    select(".vHoverLineopen-interest")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    if (highlightCandles) {
      const yt2 = _this.svgNode.y;
      const yt3 = _this.svgNodeOI.y;
      select(".dotcommitmentTraders")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt2(selData.close)})`
        );
      select(".barOIHist")
        .style("opacity", 1)
        .attr("x", xPix - candleWidth / 2)
        .attr("y", Math.min(yt3(0), yt3(selData["open_interest"])))
        .attr(
          "height",
          Math.max(1, Math.abs(yt3(0) - yt3(selData["open_interest"])))
        )
        .attr("width", candleWidth);
    } else {
      select(".dotcommitmentTraders").style("opacity", 0);
      select(".barOIHist").style("opacity", 0);
    }
    displayDataCOT(selData, _this);
  }
  function mouseout() {
    select(".vHoverLinecommitmentTraders").style("opacity", 0);
    select(".dotcommitmentTraders").style("opacity", 0);
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.hide();
    }
    select(".vHoverLineopen-interest").style("opacity", 0);
    select(".barOIHist").style("opacity", 0);
    displayDataCOT(
      _this.allData.historicalSliced[_this.allData.historicalSliced.length - 1],
      _this
    );
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.hide();
    }

    select(".vHoverLinecommitmentTraders").style("opacity", 0);
    select(".dotcommitmentTraders").style("opacity", 0);
    if (_this.userSelections["showComm"]) {
      select(".barCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonComm"]) {
      select(".barNCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonRep"]) {
      select(".barNRHistogram").style("opacity", 0);
    }
    select(".hHoverLineNet").style("opacity", 0);
    select(".vHoverLineNet").style("opacity", 0);
    select(".barOIHist").style("opacity", 0);
    select(".vHoverLineopen-interest").style("opacity", 0);

    _this.transform = event.transform;
    _this.svgNodeNet.gX.call(
      _this.svgNodeNet.xAxis.scale(_this.transform.rescaleX(_this.svgNodeNet.x))
    );
    const xt = _this.transform.rescaleX(_this.svgNodeNet.x);
    let yt = _this.svgNodeNet.y;
    NetZooming(_this.transform, xt, yt, _this, barWidth);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentCOT").call(_this.zoomCOT.transform, _this.transform);
      select(".hoverParentOI").call(_this.zoomOI.transform, _this.transform);

      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNodeNet.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      let xmin = new Date(xt.invert(0));
      let xmax = new Date(xt.invert(_this.svgNodeNet.width));

      if (!_this.externalZoom) {
        let data = _this.allData.historical.map(d => d);
        _this.allData.historicalSliced = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }

      let minMaxYAxis = _this.getMinMaxNet(_this.allData.historicalSliced);
      if (!isFinite(minMaxYAxis[0])) {
        minMaxYAxis[0] = -1;
      }
      if (!isFinite(minMaxYAxis[1])) {
        minMaxYAxis[1] = 1;
      }
      _this.svgNodeNet.y.domain(minMaxYAxis);
      let yt = _this.svgNodeNet.y;

      NetZoomEnd(
        _this.transform,
        xt,
        yt,
        _this.allData.historicalSliced,
        _this,
        barWidth
      );
    }, 200);
  }

  let resizeTimer;
  zoomSetup(
    _this,
    "svgNode" + auxName,
    "zoom" + auxName,
    auxName,
    "",
    mouseover,
    mouseout,
    zoomed,
    zoomEnd
  );
}

export function NetZooming(t, xt, yt, _this, barWidth = 0.7) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;

  if (_this.userSelections["showNonComm"]) {
    _this.svgNodeNet["NCHistogram"]
      .attr("x", d => xt(d.date) - candleWidth / 2)
      .attr("y", function(d) {
        return Math.min(yt(0), yt(d["non_commercial_net"]));
      })
      .attr("width", candleWidth);
  }
  if (_this.userSelections["showNonRep"]) {
    _this.svgNodeNet["NRHistogram"]
      .attr("x", d => xt(d.date) - candleWidth / 2)
      .attr("y", function(d) {
        return Math.min(yt(0), yt(d["non_rep_net"]));
      })
      .attr("width", candleWidth);
  }
  if (_this.userSelections["showComm"]) {
    _this.svgNodeNet["CHistogram"]
      .attr("x", d => xt(d.date) - candleWidth / 2)
      .attr("y", function(d) {
        return Math.min(yt(0), yt(d["commercial_net"]));
      })
      .attr("width", candleWidth);
  }
  if (_this.userSelections["showNonComm"]) {
    _this.svgNodeNet["cotHighlight"]
      .attr("x", d => xt(d.date) - candleWidth / 2)
      .attr("y", function(d) {
        return yt(d["cotHighlight"]);
      })
      .attr("width", candleWidth);
  }
  _this.svgNodeNet["zeroLineNet"].attr("y1", yt(0)).attr("y2", yt(0));
}

export function NetZoomEnd(t, xt, yt, filtered, _this, barWidth = 0.7) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;

  for (let zoomElement of _this.D3ChartNET.zoomElements) {
    zoomElement.recalculate({
      data: filtered,
      candleWidth,
      yt,
      xt,
      customNameBar: zoomElement.customName
    });
  }

  _this.svgNodeNet["zeroLineNet"]
    .attr("y1", _this.svgNodeNet.y(0))
    .attr("y2", _this.svgNodeNet.y(0));

  let tickCount = _this.svgNodeNet.height / 40;
  _this.svgNodeNet.gY
    .transition()
    .duration(500)
    .call(
      axisRight()
        .tickSize(-_this.svgNode.width)
        .ticks(tickCount)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount)
        .scale(yt)
    );
}
